import { useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { getInvoiceSettings, saveInvoiceSettings } from '../api/endpoints/invoices';
import { createIdempotencyKeyHolder } from '../api/idempotency';
import { Switch } from '../features/permissions/Switch';
import { ErrorBanner } from './ErrorBanner';

export const INVOICE_SETTINGS_QUERY_KEY = ['business-invoice-settings'];

const TITLE_ID = 'auto-invoice-title';
const HINT_ID = 'auto-invoice-hint';

/**
 * "Emitir factura al cobrar". Rendered only inside the datos fiscales section,
 * which is already gated on `negocio`, the one key the API lets write it.
 * Each toggle is its own idempotent PUT; the switch moves once the server
 * agrees, so a refused save leaves it where it was.
 */
export function AutoInvoiceSwitch() {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({ queryKey: INVOICE_SETTINGS_QUERY_KEY, queryFn: getInvoiceSettings });
  const saveKey = useRef(createIdempotencyKeyHolder()).current;
  const mutation = useMutation({
    mutationFn: (autoInvoice: boolean) => saveInvoiceSettings({ autoInvoice }, saveKey.ensure()),
    meta: { exito: (autoInvoice) => (autoInvoice ? 'Facturas automáticas activadas' : 'Facturas automáticas apagadas') },
    onSuccess: (settings) => queryClient.setQueryData(INVOICE_SETTINGS_QUERY_KEY, settings),
    onSettled: () => saveKey.reset(),
  });

  return (
    <>
      <div className="group auto-invoice">
        <div className="row permisos-row">
          <div className="grow">
            <p className="row-title" id={TITLE_ID}>
              Emitir factura al cobrar
            </p>
            <p className="row-sub" id={HINT_ID}>
              Cada cobro sale con su factura, sin que nadie en caja tenga que acordarse.
            </p>
          </div>
          <Switch
            checked={settingsQuery.data?.autoInvoice ?? false}
            labelledBy={TITLE_ID}
            describedBy={HINT_ID}
            disabled={!settingsQuery.data || mutation.isPending}
            onChange={(next) => mutation.mutate(next)}
          />
        </div>
      </div>
      {settingsQuery.isError ? <ErrorBanner message={errorMessage(settingsQuery.error)} /> : null}
      {mutation.isError ? <ErrorBanner message={errorMessage(mutation.error)} /> : null}
    </>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos guardar el cambio. Intenta de nuevo.';
}
